<script setup>
/**
 * Ajustes › Plantillas de Mensajes (Victor 2026-05-23). CRUD del módulo nuevo.
 * Estilo M1.B. SOLO gestiona la tabla message_templates · no toca nada existente.
 */
import { ref, computed, onMounted, reactive } from 'vue';
import { useStore } from 'vuex';
import MessageTemplatesAPI from 'dashboard/api/messageTemplates';

const store = useStore();

const CATEGORIES = [
  'Saludo inicial', 'Información de paquetes', 'Confirmación de cita', 'Recordatorio de sesión',
  'Ubicación de sucursal', 'Requisitos para la sesión', 'Formas de pago', 'Seguimiento a cliente',
  'Cliente sin respuesta', 'Despedida profesional', 'Promociones', 'Renta de vestidos',
  'Maquillaje y peinado', 'Reprogramación', 'Cuentas por cobrar',
];
const VARIABLES = [
  'nombre_cliente', 'nombre_agente', 'sucursal', 'fecha_cita', 'hora_cita',
  'nombre_paquete', 'monto_pendiente', 'ubicacion_sucursal', 'telefono_sucursal',
];

const templates = ref([]);
const loading = ref(true);
const search = ref('');
const catFilter = ref(null);
const showModal = ref(false);
const saving = ref(false);
const editingId = ref(null);
const form = reactive({
  title: '', category: CATEGORIES[0], body: '', branch_id: null,
  visibility: 'general', active: true,
});

const branches = computed(() =>
  (store.getters['branches/getBranches'] || []).filter(b => !b.is_personal)
);

const filtered = computed(() => {
  let list = templates.value;
  if (catFilter.value) list = list.filter(t => t.category === catFilter.value);
  if (search.value) {
    const q = search.value.toLowerCase();
    list = list.filter(t => (t.title || '').toLowerCase().includes(q) || (t.body || '').toLowerCase().includes(q));
  }
  return list;
});

const branchLabel = id => {
  if (!id) return 'Todas';
  return branches.value.find(b => b.id === id)?.code || `#${id}`;
};

const fetchTemplates = async () => {
  loading.value = true;
  try {
    const { data } = await MessageTemplatesAPI.get();
    templates.value = data || [];
  } catch (e) {
    templates.value = [];
  } finally {
    loading.value = false;
  }
};

const openCreate = () => {
  editingId.value = null;
  Object.assign(form, { title: '', category: CATEGORIES[0], body: '', branch_id: null, visibility: 'general', active: true });
  showModal.value = true;
};
const openEdit = t => {
  editingId.value = t.id;
  Object.assign(form, {
    title: t.title, category: t.category || CATEGORIES[0], body: t.body,
    branch_id: t.branch_id, visibility: t.visibility || 'general', active: t.active,
  });
  showModal.value = true;
};
const closeModal = () => { showModal.value = false; };

const insertVar = v => {
  form.body += `{${v}}`;
};

const save = async () => {
  if (!form.title.trim() || !form.body.trim()) return;
  saving.value = true;
  try {
    const payload = { message_template: { ...form } };
    if (editingId.value) await MessageTemplatesAPI.update(editingId.value, payload);
    else await MessageTemplatesAPI.create(payload);
    showModal.value = false;
    await fetchTemplates();
  } catch (e) {
    /* noop */
  } finally {
    saving.value = false;
  }
};

const remove = async t => {
  if (t.pinned || t.id < 0) return; // la presentación fija no se borra
  // eslint-disable-next-line no-alert
  if (!window.confirm(`¿Eliminar la plantilla "${t.title}"?`)) return;
  await MessageTemplatesAPI.delete(t.id);
  await fetchTemplates();
};

const previewBody = computed(() =>
  (form.body || '')
    .replace(/\{nombre_cliente\}/g, 'María Pérez')
    .replace(/\{nombre_agente\}/g, 'Karina')
    .replace(/\{sucursal\}/g, branchLabel(form.branch_id))
);

onMounted(() => {
  store.dispatch('branches/get');
  fetchTemplates();
});
</script>

<template>
  <div class="mt">
    <div class="head">
      <div>
        <h1>✦ Plantillas de Mensajes</h1>
        <div class="sub">Mensajes predefinidos para enviar rápido al cliente · por sucursal y categoría</div>
      </div>
      <button class="btn btn-gold" @click="openCreate">+ Nueva plantilla</button>
    </div>

    <div class="toolbar">
      <div class="search">
        <span class="ic">🔎</span>
        <input v-model="search" placeholder="Buscar plantilla…" />
      </div>
      <div class="chips">
        <span class="chip" :class="{ on: !catFilter }" @click="catFilter = null">Todas</span>
        <span v-for="c in CATEGORIES.slice(0, 8)" :key="c" class="chip" :class="{ on: catFilter === c }" @click="catFilter = c">{{ c }}</span>
      </div>
    </div>

    <div v-if="loading" class="empty">Cargando plantillas…</div>
    <div v-else-if="!filtered.length" class="empty card">
      Aún no hay plantillas. Crea la primera con “+ Nueva plantilla”.
    </div>
    <div v-else class="card list">
      <div class="lhead">
        <span>Plantilla</span><span>Categoría</span><span>Sucursal</span><span>Creada / Modificada</span><span class="rgt">Estado</span>
      </div>
      <div v-for="t in filtered" :key="t.id" class="lrow" :class="{ pinned: t.pinned }">
        <div class="tname">
          <span v-if="t.pinned" class="pinbadge">📌 Obligatoria</span>{{ t.title }}
          <div class="tprev">{{ t.body }}</div>
        </div>
        <div><span class="cpill">{{ t.category || '—' }}</span></div>
        <div><span class="bpill" :class="{ lr: t.branch_id }">{{ branchLabel(t.branch_id) }}</span></div>
        <div class="meta">Creó <b>{{ t.created_by || '—' }}</b><br />Editó <b>{{ t.updated_by || '—' }}</b></div>
        <div class="acts">
          <span class="stat" :class="t.active ? 'on' : 'off'">{{ t.active ? '● Activa' : '○ Inactiva' }}</span>
          <template v-if="t.pinned">
            <span class="lockmsg" title="Plantilla fija del sistema">🔒 Fija · primera por regla</span>
          </template>
          <template v-else>
            <button class="mini" title="Editar" @click="openEdit(t)">✏️</button>
            <button class="mini" title="Eliminar" @click="remove(t)">🗑</button>
          </template>
        </div>
      </div>
    </div>

    <!-- modal crear/editar -->
    <div v-if="showModal" class="ov" @click.self="closeModal">
      <div class="modal card">
        <div class="mh">
          <div class="mtt">{{ editingId ? 'Editar plantilla' : 'Nueva plantilla' }}</div>
          <button class="mx" @click="closeModal">✕</button>
        </div>
        <div class="field">
          <label>Nombre</label>
          <input v-model="form.title" class="finput" placeholder="Ej. Saludo inicial" />
        </div>
        <div class="row2">
          <div class="field">
            <label>Categoría</label>
            <select v-model="form.category" class="finput">
              <option v-for="c in CATEGORIES" :key="c" :value="c">{{ c }}</option>
            </select>
          </div>
          <div class="field">
            <label>Sucursal</label>
            <select v-model="form.branch_id" class="finput">
              <option :value="null">Todas las sucursales</option>
              <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
            </select>
          </div>
        </div>
        <div class="row2">
          <div class="field">
            <label>Visibilidad</label>
            <select v-model="form.visibility" class="finput">
              <option value="general">General (toda la sucursal)</option>
              <option value="private">Privada (solo yo)</option>
              <option value="team">Por equipo</option>
            </select>
          </div>
          <div class="field">
            <label>Estado</label>
            <select v-model="form.active" class="finput">
              <option :value="true">Activa</option>
              <option :value="false">Inactiva</option>
            </select>
          </div>
        </div>
        <div class="field">
          <label>Mensaje</label>
          <textarea v-model="form.body" class="ftext" placeholder="Escribe el mensaje…"></textarea>
          <div class="varbar">
            <span v-for="v in VARIABLES" :key="v" class="varchip" @click="insertVar(v)">{{ '{' + v + '}' }}</span>
          </div>
          <div class="hint">Toca una variable para insertarla · se reemplaza sola al usar la plantilla.</div>
        </div>
        <div class="prevbox">
          <div class="pl">Vista previa</div>
          <div class="pt">{{ previewBody || '—' }}</div>
        </div>
        <div class="mfoot">
          <button class="btn btn-sec" @click="closeModal">Cancelar</button>
          <button class="btn btn-gold" :disabled="saving" @click="save">{{ saving ? 'Guardando…' : 'Guardar plantilla' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.mt {
  --gold: #fbbf24; --gold-d: #d97706; --g: #22c55e; --r: #ef4444; --blue: #60a5fa;
  --w55: rgba(255, 255, 255, 0.55); --w40: rgba(255, 255, 255, 0.4);
  --card: rgba(10, 10, 10, 0.88); --bd: rgba(255, 255, 255, 0.1);
  font-family: -apple-system, system-ui, sans-serif; color: #fff;
  background: radial-gradient(ellipse 80% 50% at 50% 0%, rgba(251, 191, 36, 0.06), transparent 70%), #050505;
  border-radius: 16px; padding: 22px 24px 40px; width: 100%; min-height: 100%;
}
.head { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 18px; flex-wrap: wrap; }
h1 { font-size: 21px; font-weight: 800; letter-spacing: -0.4px; }
.sub { font-size: 12px; color: var(--w40); font-weight: 600; margin-top: 4px; }
.btn { border: none; cursor: pointer; font-family: inherit; font-weight: 800; border-radius: 10px; transition: 0.18s; }
.btn-gold { background: linear-gradient(135deg, var(--gold), var(--gold-d)); color: #000; padding: 10px 16px; font-size: 12.5px; box-shadow: 0 0 16px rgba(251, 191, 36, 0.3); }
.btn-gold:hover { transform: translateY(-2px); } .btn-gold:disabled { opacity: 0.6; cursor: default; transform: none; }
.btn-sec { background: rgba(255, 255, 255, 0.06); border: 1px solid rgba(255, 255, 255, 0.12); color: #fff; padding: 10px 15px; font-size: 12.5px; }
.toolbar { display: flex; gap: 12px; align-items: center; margin-bottom: 16px; flex-wrap: wrap; }
.search { display: flex; align-items: center; gap: 9px; background: var(--card); border: 1px solid var(--bd); border-radius: 10px; padding: 9px 13px; min-width: 260px; }
.search input { background: none; border: none; outline: none; color: #fff; font-size: 13px; font-family: inherit; width: 100%; }
.search input::placeholder { color: var(--w40); } .ic { color: var(--w40); }
.chips { display: flex; gap: 6px; flex-wrap: wrap; }
.chip { padding: 6px 11px; border-radius: 8px; font-size: 11px; font-weight: 700; color: var(--w55); background: var(--card); border: 1px solid var(--bd); cursor: pointer; transition: 0.15s; }
.chip:hover { color: #fff; } .chip.on { color: var(--gold); background: rgba(251, 191, 36, 0.1); border-color: rgba(251, 191, 36, 0.3); }
.card { position: relative; background: var(--card); border: 1.5px solid var(--bd); border-radius: 16px; box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.04) inset, 0 14px 36px rgba(0, 0, 0, 0.55); overflow: hidden; }
.card::before { content: ''; position: absolute; inset: 0; z-index: 1; pointer-events: none; border-radius: inherit; background: linear-gradient(120deg, transparent 12%, rgba(255, 255, 255, 0.07) 22%, transparent 24%), radial-gradient(circle at 100% 0%, rgba(255, 255, 255, 0.1), transparent 60%); }
.card > * { position: relative; z-index: 2; }
.empty { padding: 40px; text-align: center; color: var(--w55); font-size: 13px; }
.list { padding: 4px 6px; }
.lhead, .lrow { display: grid; grid-template-columns: 1.6fr 150px 110px 1fr 150px; gap: 14px; align-items: center; }
.lhead { padding: 11px 14px; font-size: 9.5px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.8px; color: var(--w40); }
.lhead .rgt { text-align: right; }
.lrow { padding: 13px 14px; border-radius: 12px; transition: 0.15s; }
.lrow:hover { background: rgba(251, 191, 36, 0.04); }
.lrow + .lrow { border-top: 1px solid rgba(255, 255, 255, 0.04); }
.tname { font-size: 13.5px; font-weight: 800; }
.tprev { font-size: 11px; color: var(--w40); font-weight: 500; margin-top: 3px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 340px; }
.cpill { display: inline-flex; padding: 3px 9px; border-radius: 6px; background: rgba(96, 165, 250, 0.1); color: var(--blue); border: 1px solid rgba(96, 165, 250, 0.3); font-size: 10px; font-weight: 800; }
.bpill { display: inline-flex; padding: 3px 9px; border-radius: 6px; background: rgba(251, 191, 36, 0.1); color: var(--gold); border: 1px solid rgba(251, 191, 36, 0.3); font-size: 10px; font-weight: 800; }
.bpill.lr { background: rgba(34, 197, 94, 0.1); border-color: rgba(34, 197, 94, 0.3); color: var(--g); }
.meta { font-size: 11px; color: var(--w55); } .meta b { color: #fff; font-weight: 700; }
.acts { display: flex; gap: 6px; justify-content: flex-end; align-items: center; }
.stat { display: inline-flex; padding: 3px 9px; border-radius: 999px; font-size: 9px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; }
.stat.on { background: rgba(34, 197, 94, 0.14); color: var(--g); border: 1px solid rgba(34, 197, 94, 0.35); }
.stat.off { background: rgba(255, 255, 255, 0.06); color: var(--w40); border: 1px solid var(--bd); }
.mini { width: 30px; height: 30px; border-radius: 8px; display: grid; place-items: center; font-size: 13px; border: 1px solid var(--bd); background: rgba(255, 255, 255, 0.04); cursor: pointer; transition: 0.15s; }
.mini:hover { border-color: rgba(251, 191, 36, 0.3); background: rgba(251, 191, 36, 0.08); }
.ov { position: fixed; inset: 0; z-index: 50; background: rgba(0, 0, 0, 0.6); backdrop-filter: blur(7px); display: grid; place-items: center; padding: 30px; }
.modal { width: min(680px, 100%); max-height: 88vh; overflow: auto; padding: 24px 26px; }
.mh { display: flex; align-items: center; justify-content: space-between; margin-bottom: 18px; }
.mtt { font-size: 18px; font-weight: 800; }
.mx { width: 32px; height: 32px; border-radius: 9px; border: 1px solid var(--bd); background: rgba(255, 255, 255, 0.04); color: var(--w55); cursor: pointer; font-size: 15px; }
.field { margin-bottom: 15px; }
.field label { font-size: 10.5px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.7px; color: var(--w40); margin-bottom: 7px; display: block; }
.finput, .ftext { width: 100%; background: rgba(255, 255, 255, 0.04); border: 1px solid var(--bd); border-radius: 10px; padding: 11px 13px; color: #fff; font-size: 13px; font-family: inherit; outline: none; }
.finput:focus, .ftext:focus { border-color: rgba(251, 191, 36, 0.4); }
.ftext { min-height: 96px; resize: vertical; line-height: 1.6; }
.row2 { display: grid; grid-template-columns: 1fr 1fr; gap: 13px; }
.varbar { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 8px; }
.varchip { font-size: 10.5px; font-weight: 700; color: var(--blue); background: rgba(96, 165, 250, 0.1); border: 1px solid rgba(96, 165, 250, 0.3); border-radius: 7px; padding: 4px 9px; cursor: pointer; transition: 0.15s; }
.varchip:hover { background: rgba(96, 165, 250, 0.2); }
.hint { font-size: 10.5px; color: var(--w40); margin-top: 7px; }
.prevbox { padding: 13px 15px; border-radius: 12px; background: rgba(34, 197, 94, 0.06); border: 1px solid rgba(34, 197, 94, 0.2); }
.prevbox .pl { font-size: 9px; font-weight: 800; letter-spacing: 1px; text-transform: uppercase; color: var(--g); margin-bottom: 6px; }
.prevbox .pt { font-size: 13px; line-height: 1.6; }
.mfoot { display: flex; gap: 9px; justify-content: flex-end; margin-top: 18px; }
.lrow.pinned { background: rgba(201, 163, 90, 0.06); border: 1px solid rgba(201, 163, 90, 0.30); }
.pinbadge { display: inline-block; margin-right: 8px; padding: 2px 8px; border-radius: 999px; font-size: 9px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; color: #c9a35a; background: rgba(201, 163, 90, 0.12); border: 1px solid rgba(201, 163, 90, 0.45); }
.lockmsg { font-size: 10px; font-weight: 700; color: rgba(201, 163, 90, 0.85); white-space: nowrap; }
</style>
